import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import Header from "../../../components/Header";
import { useContext } from "react";
import { AuthContext } from "../../utils/AuthContext";
import toast from "react-hot-toast";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { TbCalendar, TbMoon, TbShieldCheck, TbShieldX, TbShieldHalf } from "react-icons/tb";

function Checkout() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const roomId = params.get("room");
  const checkIn = params.get("checkIn");
  const checkOut = params.get("checkOut");
  const ratePlanId = params.get("ratePlanId");

  const [room, setRoom] = useState(null);
  const [ratePlan, setRatePlan] = useState(null);
  const [total, setTotal] = useState(null);
  const [pricePerNight, setPricePerNight] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loadingReserve, setLoadingReserve] = useState(false);
  const [isUserReady, setIsUserReady] = useState(false);

  const [guest, setGuest] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: ""
  });

  const nights = checkIn && checkOut
    ? Math.ceil((new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24))
    : 0;

  // Fetch user
  useEffect(() => {
    if (!user) { setIsUserReady(true); return; }
    const fetchUser = async () => {
      try {
        const res = await axios.get(
          `https://api.komorotel.com/api/user/${user?._id}`,
          { withCredentials: true }
        );
        setUserData(res.data);
      } catch (err) { console.log(err); }
    };
    fetchUser();
  }, [user]);

  useEffect(() => {
    if (!userData) return;
    setGuest({
      firstName: userData.name || "",
      lastName: userData.surname || "",
      email: userData.email || "",
      phone: userData.phone || ""
    });
    setIsUserReady(true);
  }, [userData]);

  // Fetch room
  useEffect(() => {
    if (!roomId) return;
    const fetchRoom = async () => {
      try {
        const res = await axios.get(`https://api.komorotel.com/api/rooms/${roomId}`);
        setRoom(res.data);
      } catch (err) { console.log(err); }
    };
    fetchRoom();
  }, [roomId]);

  // Fetch prix + rate plan
  useEffect(() => {
    if (!roomId || !ratePlanId || !checkIn || !checkOut) return;
    const fetchPrice = async () => {
      try {
        const res = await axios.get(
          `https://api.komorotel.com/api/rateprices/calculate`,
          { params: { roomId, ratePlanId, checkIn, checkOut } }
        );
        setTotal(res.data.total);
        setPricePerNight(res.data.pricePerNight);
        // On récupère aussi le rate plan pour afficher les infos
        const rp = await axios.get(
          `https://api.komorotel.com/api/rateplans/single/${ratePlanId}`,
          { withCredentials: true }
        );
        setRatePlan(rp.data);
      } catch (err) { console.log(err); }
    };
    fetchPrice();
  }, [roomId, ratePlanId, checkIn, checkOut]);

  // Politique annulation
  const getPolicyInfo = () => {
    if (!ratePlan) return null;
    const policy = ratePlan.cancellationPolicy;
    const diffHours = (new Date(checkIn) - new Date()) / (1000 * 60 * 60);

    if (policy.type === "free") {
      const isFree = diffHours >= policy.hoursBeforeArrival;
      return {
        icon: <TbShieldCheck className="text-green-500 text-lg shrink-0" />,
        text: `Annulation gratuite jusqu'à ${policy.hoursBeforeArrival}h avant`,
        sub: isFree ? "Remboursement total si annulation maintenant" : "Non remboursable à cette date",
        color: isFree ? "text-green-600" : "text-red-500"
      };
    }
    if (policy.type === "partial") {
      return {
        icon: <TbShieldHalf className="text-yellow-500 text-lg shrink-0" />,
        text: `Annulation partielle — ${policy.penaltyPercentage}% de frais après ${policy.hoursBeforeArrival}h`,
        sub: `Remboursement ${100 - policy.penaltyPercentage}% selon conditions`,
        color: "text-yellow-600"
      };
    }
    if (policy.type === "non_refundable") {
      return {
        icon: <TbShieldX className="text-red-500 text-lg shrink-0" />,
        text: "Non remboursable",
        sub: "Aucun remboursement en cas d'annulation",
        color: "text-red-500"
      };
    }
    return null;
  };

  const policyInfo = getPolicyInfo();

  const handleReserve = async () => {
    try {
      setLoadingReserve(true);

      if (!guest.email || !guest.firstName || !guest.lastName) {
        toast.error("Veuillez remplir tous les champs obligatoires");
        setLoadingReserve(false);
        return;
      }

      if (!guest.phone || !guest.phone.startsWith("+")) {
        toast.error("Numéro de téléphone invalide");
        setLoadingReserve(false);
        return;
      }

      const res = await axios.post(
        "https://api.komorotel.com/api/reservation",
        { roomId, ratePlanId, checkInDate: checkIn, checkOutDate: checkOut, guest },
        { withCredentials: true }
      );

      const reservation = res.data;

      if (reservation.status === "pending") {
        navigate("/reservation-pending", { state: { email: reservation.guest.email } });
      } else {
        navigate("/confirmation/" + reservation._id);
      }

    } catch (err) {
      toast.error(err.response?.data?.error || "Une erreur est survenue");
    } finally {
      setLoadingReserve(false);
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-[#F7F7F5] pt-24 pb-16 px-4">
        <div className="max-w-5xl mx-auto">

          {/* TITRE */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900">Finaliser la réservation</h1>
            <p className="text-sm text-gray-500 mt-1">Vérifiez les détails avant de confirmer</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">

            {/* GAUCHE — RÉSUMÉ */}
            <div className="flex flex-col gap-4">

              {/* Room */}
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
                  Votre séjour
                </h2>

                {room && (
                  <div className="flex gap-4">
                    <img
                      src={room.photos?.[0]}
                      alt=""
                      className="w-20 h-20 object-cover rounded-xl shrink-0"
                    />
                    <div>
                      <p className="font-semibold text-gray-900">{room.title}</p>
                      <div className="flex items-center gap-1.5 text-sm text-gray-500 mt-1">
                        <TbCalendar className="shrink-0" />
                        <span>{checkIn} → {checkOut}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-sm text-gray-500 mt-0.5">
                        <TbMoon className="shrink-0" />
                        <span>{nights} nuit{nights > 1 && "s"}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Tarif choisi */}
              {ratePlan && (
                <div className="bg-white rounded-2xl border border-gray-100 p-5">
                  <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
                    Tarif sélectionné
                  </h2>

                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-gray-900">{ratePlan.name}</p>

                      {ratePlan.mealPlan && ratePlan.mealPlan !== "none" && (
                        <p className="text-xs text-gray-500 mt-1">
                          {ratePlan.mealPlan === "breakfast" && "🍳 Petit-déjeuner inclus"}
                          {ratePlan.mealPlan === "half_board" && "🍽️ Demi-pension"}
                          {ratePlan.mealPlan === "full_board" && "🍽️ Pension complète"}
                        </p>
                      )}
                    </div>

                    <div className="text-right">
                      <p className="font-bold text-gray-900 text-lg">{pricePerNight}€</p>
                      <p className="text-xs text-gray-400">/ nuit</p>
                    </div>
                  </div>

                  {/* Politique annulation */}
                  {policyInfo && (
                    <div className="mt-4 pt-4 border-t border-gray-50">
                      <div className="flex items-start gap-2">
                        {policyInfo.icon}
                        <div>
                          <p className={`text-xs font-medium ${policyInfo.color}`}>
                            {policyInfo.text}
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {policyInfo.sub}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Total */}
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-gray-500">Total du séjour</p>
                    <p className="text-xs text-gray-400">{nights} nuit{nights > 1 && "s"} × {pricePerNight}€</p>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {total ? `${total}€` : "..."}
                  </p>
                </div>
              </div>

            </div>

            {/* DROITE — FORMULAIRE */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
                Informations client
              </h2>

              {!isUserReady ? (
                <div className="flex flex-col gap-3 animate-pulse">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="h-10 bg-gray-100 rounded-lg" />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col gap-3">

                  <input
                    value={guest.lastName}
                    onChange={(e) => setGuest({ ...guest, lastName: e.target.value })}
                    placeholder="Nom *"
                    className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#3A9D23] transition"
                  />

                  <input
                    value={guest.firstName}
                    onChange={(e) => setGuest({ ...guest, firstName: e.target.value })}
                    placeholder="Prénom *"
                    className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#3A9D23] transition"
                  />

                  <input
                    value={guest.email}
                    onChange={(e) => setGuest({ ...guest, email: e.target.value })}
                    placeholder="Email *"
                    type="email"
                    className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#3A9D23] transition"
                  />

                  <PhoneInput
                    country={"km"}
                    value={guest.phone?.replace("+", "")}
                    onChange={(phone) => setGuest(prev => ({ ...prev, phone: "+" + phone }))}
                    inputStyle={{
                      width: "100%",
                      height: "42px",
                      borderRadius: "12px",
                      border: "1px solid #e5e7eb",
                      fontSize: "14px"
                    }}
                    buttonStyle={{
                      borderTopLeftRadius: "12px",
                      borderBottomLeftRadius: "12px",
                      border: "1px solid #e5e7eb",
                      borderRight: "none"
                    }}
                  />

                  {user && (
                    <p className="text-xs text-gray-400">
                      ✓ Informations pré-remplies depuis votre compte
                    </p>
                  )}

                </div>
              )}

              {/* CTA */}
              <button
                disabled={loadingReserve}
                onClick={handleReserve}
                className={`
                  mt-6 w-full py-3.5 rounded-xl font-semibold text-sm transition
                  ${loadingReserve
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-[#3A9D23] text-white hover:bg-[#33891E] active:scale-[0.98]"
                  }
                `}
              >
                {loadingReserve ? "Réservation en cours..." : "Confirmer la réservation"}
              </button>

              <p className="text-xs text-center text-gray-400 mt-3">
                En confirmant, vous acceptez les conditions du tarif sélectionné.
              </p>

            </div>

          </div>

        </div>
      </div>
    </>
  );
}

export default Checkout;